import React from 'react';
import { translate } from 'react-jhipster';
import dayjs from 'dayjs';
import { useAppSelector } from 'app/config/store';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

export type RebateScheduleFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';

/**
 * `period` is the transaction period the document covers. 'DEFAULT' is always the previous
 * day/week/month/quarter/year; the other values depend on the frequency:
 * WEEKLY 'FLEXIBLE' (7 days before the run day), MONTHLY '1'-'12' (a month),
 * QUARTERLY 'Q1'-'Q4', YEARLY a year such as '2025'. DAILY only has 'DEFAULT'.
 */
export interface IRebateSchedule {
  frequency: RebateScheduleFrequency | '';
  dayOfWeek: string;
  month: string;
  date: string;
  time: string;
  period: string;
  /** IANA zone `time` (and the date fields) are in, e.g. 'Asia/Jakarta'. Stamped on save; absent on older schedules (backend treats those as Asia/Kuala_Lumpur). */
  timeZone?: string;
}

export const emptySchedule: IRebateSchedule = {
  frequency: '', dayOfWeek: '', month: '', date: '', time: '', period: '',
};

/** The browser's IANA time zone, e.g. 'Asia/Jakarta'. */
export const browserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Label like "Asia/Jakarta (UTC+07:00)". */
export const timeZoneLabel = (timeZone: string) => {
  try {
    const ref = new Date();
    ref.setSeconds(0, 0);
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).formatToParts(ref);
    const part = (type: string) => Number(parts.find(p => p.type === type)?.value);
    const wallClockAsUtc = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour') % 24, part('minute'));
    const offsetMinutes = Math.round((wallClockAsUtc - ref.getTime()) / 60000);
    const abs = Math.abs(offsetMinutes);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${timeZone} (UTC${offsetMinutes < 0 ? '-' : '+'}${pad(Math.floor(abs / 60))}:${pad(abs % 60)})`;
  } catch {
    return timeZone;
  }
};

const FREQUENCIES: RebateScheduleFrequency[] = ['DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY'];

// Which fields each frequency needs, in display order. Time is always last.
export const FIELDS_BY_FREQUENCY: Record<RebateScheduleFrequency, Array<keyof IRebateSchedule>> = {
  DAILY: ['period', 'time'],
  WEEKLY: ['dayOfWeek', 'period', 'time'],
  MONTHLY: ['date', 'period', 'time'],
  QUARTERLY: ['date', 'period', 'time'],
  YEARLY: ['month', 'date', 'period', 'time'],
};

const YEARS_OFFERED = 5;

const weekdayName = (localDay: dayjs.Dayjs, index: number) => localDay.day(((index % 7) + 7) % 7).format('dddd');

const periodKey = (key: string) => `comexApp.bank.rebate.schedule.periodOptions.${key}`;

const defaultPeriodLabel = (frequency: RebateScheduleFrequency, localDay: dayjs.Dayjs) =>
  frequency === 'WEEKLY'
    ? translate(periodKey('weeklyDefault'), { from: weekdayName(localDay, 0), to: weekdayName(localDay, 6) })
    : translate(periodKey(`${frequency.toLowerCase()}Default`));

// Flexible weekly period: from the run day's weekday last week, to the day before the run day.
const weeklyFlexibleLabel = (localDay: dayjs.Dayjs, dayOfWeekStr: string) =>
  dayOfWeekStr === ''
    ? translate(periodKey('weeklyFlexibleAny'))
    : translate(periodKey('weeklyFlexible'), {
      from: weekdayName(localDay, Number(dayOfWeekStr)), to: weekdayName(localDay, Number(dayOfWeekStr) - 1),
    });

const quarterLabel = (localDay: dayjs.Dayjs, quarter: number) =>
  `Q${quarter} (${localDay.month((quarter - 1) * 3).format('MMM')} - ${localDay.month(quarter * 3 - 1).format('MMM')})`;

/** Selectable period values with their labels, DEFAULT first. */
export const periodOptions = (localDay: dayjs.Dayjs, schedule: IRebateSchedule): Array<[string, string]> => {
  const { frequency } = schedule;
  if (!frequency) {
    return [];
  }
  const options: Array<[string, string]> = [['DEFAULT', defaultPeriodLabel(frequency, localDay)]];
  switch (frequency) {
    case 'WEEKLY':
      return [...options, ['FLEXIBLE', weeklyFlexibleLabel(localDay, schedule.dayOfWeek)]];
    case 'MONTHLY':
      return [...options, ...Array.from({ length: 12 }, (_, i): [string, string] => [String(i + 1), localDay.month(i).format('MMMM')])];
    case 'QUARTERLY':
      return [...options, ...[1, 2, 3, 4].map((q): [string, string] => [`Q${q}`, quarterLabel(localDay, q)])];
    case 'YEARLY': {
      const years = Array.from({ length: YEARS_OFFERED }, (_, i) => String(localDay.year() - i));
      // Keep a saved year that has since dropped out of the offered range.
      if (/^\d{4}$/.test(schedule.period) && !years.includes(schedule.period)) {
        years.push(schedule.period);
      }
      return [...options, ...years.map((year): [string, string] => [year, year])];
    }
    default:
      return options;
  }
};

export const periodLabel = (localDay: dayjs.Dayjs, schedule: IRebateSchedule): string =>
  periodOptions(localDay, schedule).find(([value]) => value === schedule.period)?.[1] ?? '';

const requiredFields = (schedule: IRebateSchedule): Array<keyof IRebateSchedule> =>
  schedule.frequency ? ['frequency', ...FIELDS_BY_FREQUENCY[schedule.frequency]] : ['frequency'];

export const isScheduleValid = (schedule: IRebateSchedule) => requiredFields(schedule).every(field => schedule[field] !== '');

/**
 * Reads the schedule saved for a bank/commodityType. Prefers the JSON-encoded `rebateSchedule*` column;
 * falls back to mapping the legacy MONTHLY + send-dates fields where it fits, for banks configured before
 * the full schedule existed. Schedules saved before `period` existed get the default period, except
 * quarterly ones, whose old `quarter` value becomes the period.
 */
export const scheduleFromBank = (
  scheduleJson?: string | null,
  legacyFrequency?: string | null,
  legacySendDates?: string | null
): IRebateSchedule => {
  if (scheduleJson) {
    try {
      const { quarter, ...parsed } = JSON.parse(scheduleJson) ?? {};
      if (FREQUENCIES.includes(parsed.frequency)) {
        const legacyQuarter = parsed.frequency === 'QUARTERLY' && /^Q[1-4]$/.test(quarter) ? quarter : 'DEFAULT';
        return { ...emptySchedule, ...parsed, period: parsed.period || legacyQuarter };
      }
    } catch {
      // not valid JSON; fall through to the legacy mapping below
    }
  }
  return legacyFrequency === 'MONTHLY' && legacySendDates
    ? { ...emptySchedule, frequency: 'MONTHLY', date: legacySendDates.split(',')[0], period: 'DEFAULT' }
    : emptySchedule;
};

interface ScheduleSectionProps {
  value: IRebateSchedule;
  onChange: (value: IRebateSchedule) => void;
  showErrors: boolean;
}

export const ScheduleSection = ({ value, onChange, showErrors }: ScheduleSectionProps) => {
  const currentLocale = useAppSelector(state => state.locale.currentLocale);
  const localDay = dayjs().locale(currentLocale || 'en');

  const set = (field: keyof IRebateSchedule) => event => onChange({ ...value, [field]: event.target.value });

  // Changing the frequency clears the fields that belong to the old one and starts on the default period.
  const setFrequency = event => onChange({ ...emptySchedule, frequency: event.target.value, time: value.time, period: 'DEFAULT' });

  const hasError = (field: keyof IRebateSchedule) => showErrors && requiredFields(value).includes(field) && value[field] === '';
  const helper = (field: keyof IRebateSchedule) => (hasError(field) ? translate('entity.validation.required') : ' ');

  // Labels sit above the fields: the global Bootstrap `legend` style breaks MUI's floating-label notch.
  const labelled = (field: keyof IRebateSchedule, input: React.ReactNode) => (
    <>
      <Typography component="label" htmlFor={`rebate-schedule-${field}`} variant="body2" sx={{ display: 'block', fontWeight: 500, mb: 0.75 }}>
        {translate(`comexApp.bank.rebate.schedule.${field}`)}
      </Typography>
      {input}
    </>
  );

  const selectField = (
    field: keyof IRebateSchedule,
    options: Array<[string, string]>,
    onSelect = set(field),
    helperText = helper(field),
    disabled = false
  ) =>
    labelled(
      field,
      <TextField select fullWidth size="small" id={`rebate-schedule-${field}`} value={value[field]} onChange={onSelect}
                 error={hasError(field)} helperText={helperText} disabled={disabled} SelectProps={{ displayEmpty: true }}>
        <MenuItem value="" disabled>
          <Typography component="span" color="text.disabled">{translate('comexApp.bank.rebate.schedule.select')}</Typography>
        </MenuItem>
        {options.map(([optionValue, optionLabel]) => (
          <MenuItem key={optionValue} value={optionValue}>{optionLabel}</MenuItem>
        ))}
      </TextField>
    );

  const renderField = (field: keyof IRebateSchedule) => {
    switch (field) {
      case 'dayOfWeek':
        return selectField(field, Array.from({ length: 7 }, (_, i) => [String(i), localDay.day(i).format('dddd')]));
      case 'month':
        return selectField(field, Array.from({ length: 12 }, (_, i) => [String(i + 1), localDay.month(i).format('MMMM')]));
      case 'date':
        return selectField(field, Array.from({ length: 31 }, (_, i) => [String(i + 1), String(i + 1)]), set(field),
          hasError('date') ? helper('date') : Number(value.date) > 28 ? translate('comexApp.bank.rebate.schedule.lastDayHint') : ' ');
      case 'period':
        // Daily always covers the previous day, so its single option is shown but not editable.
        return selectField(field, periodOptions(localDay, value), set(field), helper(field), value.frequency === 'DAILY');
      default: {
        // The time is saved in the browser's zone; warn when that differs from the zone it was last saved in.
        const zoneHint = value.timeZone && value.timeZone !== browserTimeZone()
          ? translate('comexApp.bank.rebate.schedule.timeZoneChanged', { saved: timeZoneLabel(value.timeZone), current: timeZoneLabel(browserTimeZone()) })
          : translate('comexApp.bank.rebate.schedule.timeZoneHint', { zone: timeZoneLabel(browserTimeZone()) });
        return labelled(
          field,
          <TextField type="time" fullWidth size="small" id={`rebate-schedule-${field}`} value={value[field]} onChange={set(field)}
                     error={hasError(field)} helperText={hasError(field) ? helper(field) : zoneHint} />
        );
      }
    }
  };

  const frequencyOptions: Array<[string, string]> = FREQUENCIES.map(f =>
    [f, translate(`comexApp.bank.rebate.schedule.frequencyOptions.${f.toLowerCase()}`)]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        {selectField('frequency', frequencyOptions, setFrequency)}
      </Grid>
      {value.frequency && (
        <Grid item xs={12} md={6}>
          {renderField('period')}
        </Grid>
      )}
      {value.frequency && (
        <Grid item xs={12} container spacing={2}>
          {FIELDS_BY_FREQUENCY[value.frequency].filter(field => field !== 'period').map(field => (
            <Grid item xs={12} sm={6} md={4} key={field}>
              {renderField(field)}
            </Grid>
          ))}
        </Grid>
      )}
    </Grid>
  );
};
